import { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Camera, ImagePlus, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { QuietEmpty, StoredImage } from '../shared';
import { useConfigureWorkstation } from '@/features/workMode/hooks';
import {
  useAddStationPhoto,
  useDeleteStationPhoto,
  useStationPhotos,
  useUpdateStationPhoto,
} from '@/features/workMode/stationSetup';
import { describeWorkError } from '@/features/workMode/api';
import type { FloorWorkstation, WorkstationPhoto } from '@/features/workMode/types';

interface StationPhotosTabProps {
  station: FloorWorkstation;
  canManage: boolean;
}

/**
 * The station gallery: what the bench looks like when it is set up right,
 * the fixtures, a stocked parts tray, the finished unit. One photo is the
 * cover shown on cards and the tablet's Station sheet.
 */
export function StationPhotosTab({ station, canManage }: StationPhotosTabProps) {
  const { data: photos = [], isLoading } = useStationPhotos(station.id);
  const add = useAddStationPhoto();
  const update = useUpdateStationPhoto();
  const remove = useDeleteStationPhoto();
  const configure = useConfigureWorkstation();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(0);
  const [lightbox, setLightbox] = useState<WorkstationPhoto | null>(null);

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) {
      toast.error('Pick image files.');
      return;
    }
    setUploading(list.length);
    let position = (photos.at(-1)?.position ?? 0) + 10;
    let failed = 0;
    let first: WorkstationPhoto | null = null;
    for (const file of list) {
      try {
        const row = await add.mutateAsync({ stationId: station.id, file, position });
        if (!first) first = row;
        position += 10;
      } catch {
        failed += 1;
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (failed) toast.error(`${failed} photo${failed === 1 ? '' : 's'} could not be uploaded.`);
    else toast.success(list.length === 1 ? 'Photo added.' : `${list.length} photos added.`);
    // First photo ever doubles as the cover so cards are not blank.
    if (first && !station.cover_image_path && photos.length === 0) {
      configure.mutate({ id: station.id, coverImagePath: first.image_path });
    }
  };

  const setCover = (p: WorkstationPhoto) => {
    configure.mutate(
      { id: station.id, coverImagePath: p.image_path },
      {
        onSuccess: () => toast.success('Cover photo updated.'),
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= photos.length) return;
    const order = [...photos];
    [order[idx], order[j]] = [order[j], order[idx]];
    // Renumber with gaps so later swaps stay cheap and ties never happen.
    void Promise.all(
      order.map((p, i) => {
        const position = (i + 1) * 10;
        return p.position === position
          ? Promise.resolve()
          : update.mutateAsync({ id: p.id, stationId: station.id, position });
      }),
    ).catch((e) => toast.error(describeWorkError(e)));
  };

  const del = (p: WorkstationPhoto) => {
    if (!window.confirm('Delete this photo?')) return;
    remove.mutate(
      {
        id: p.id,
        stationId: station.id,
        imagePath: p.image_path,
        isCover: station.cover_image_path === p.image_path,
      },
      {
        onSuccess: () => {
          toast.success('Photo deleted.');
          if (lightbox?.id === p.id) setLightbox(null);
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {photos.length === 0
            ? 'Show people what a correctly set-up bench looks like.'
            : `${photos.length} photo${photos.length === 1 ? '' : 's'}. The starred one is the cover.`}
        </p>
        {canManage && (
          <>
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading > 0}>
              <ImagePlus className="h-4 w-4" />
              {uploading > 0 ? `Uploading ${uploading}…` : 'Add photos'}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                void onFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </>
        )}
      </div>

      {isLoading ? null : photos.length === 0 ? (
        <QuietEmpty>
          <span className="flex flex-col items-center gap-2">
            <Camera className="h-6 w-6" />
            No photos yet.
            {canManage && (
              <span className="text-xs">
                Phone photos are fine — they are compressed on upload.
              </span>
            )}
          </span>
        </QuietEmpty>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((p, i) => {
            const isCover = station.cover_image_path === p.image_path;
            return (
              <li
                key={p.id}
                className={cn(
                  'overflow-hidden rounded-2xl border bg-card',
                  isCover ? 'border-info' : 'border-border',
                )}
              >
                <div className="relative aspect-[4/3] bg-secondary">
                  <StoredImage
                    bucket="work-instructions"
                    path={p.image_path}
                    alt={p.caption ?? 'Station photo'}
                    className="h-full w-full rounded-none"
                    onClick={() => setLightbox(p)}
                  />
                  {isCover && (
                    <Badge variant="info" className="absolute left-2 top-2 gap-1">
                      <Star className="h-3 w-3" /> Cover
                    </Badge>
                  )}
                </div>
                <div className="space-y-2 p-3">
                  <CaptionField photo={p} stationId={station.id} canManage={canManage} />
                  {canManage && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => move(i, -1)}
                          disabled={i === 0 || update.isPending}
                          aria-label="Move earlier"
                        >
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => move(i, 1)}
                          disabled={i === photos.length - 1 || update.isPending}
                          aria-label="Move later"
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {!isCover && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCover(p)}
                            disabled={configure.isPending}
                          >
                            <Star className="h-4 w-4" />
                            Make cover
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-fresh-salmon-dark"
                          onClick={() => del(p)}
                          disabled={remove.isPending}
                          aria-label="Delete photo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogTitle className="sr-only">Station photo</DialogTitle>
          {lightbox && (
            <div>
              <StoredImage
                bucket="work-instructions"
                path={lightbox.image_path}
                alt={lightbox.caption ?? 'Station photo'}
                className="max-h-[80vh] w-full object-contain"
              />
              {lightbox.caption && (
                <p className="px-2 pb-1 pt-2 text-center text-sm text-muted-foreground">
                  {lightbox.caption}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Caption that saves on blur / Enter, so the gallery never needs a form. */
function CaptionField({
  photo,
  stationId,
  canManage,
}: {
  photo: WorkstationPhoto;
  stationId: string;
  canManage: boolean;
}) {
  const update = useUpdateStationPhoto();
  const [value, setValue] = useState(photo.caption ?? '');
  const commit = () => {
    if (value.trim() === (photo.caption ?? '')) return;
    update.mutate(
      { id: photo.id, stationId, caption: value },
      { onError: (e) => toast.error(describeWorkError(e)) },
    );
  };
  if (!canManage) {
    return (
      <p className="min-h-5 truncate text-sm text-muted-foreground">
        {photo.caption || <span className="italic">No caption</span>}
      </p>
    );
  }
  return (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      placeholder="Caption — what should people notice?"
      className="h-9"
    />
  );
}
